import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Button,
  Input,
  Avatar,
  Box,
  Text,
  Flex,
  Checkbox,
  Tabs,
  TabList,
  Tab,
  useToast,
  Spinner,
  HStack,
} from '@chakra-ui/react';
import axios from 'axios';
import useStore from '../../../../store/store';
import { getUser } from '../../../../cookie/user';
import { CartContext } from '../../../../context/cart';

export const CartDrawer = ({ btnRef }) => {
  const {
    addPatientsDrawer,
    setAddPatientsDrawer,
    addPatientToCartPlan,
    addPatient,
    patients,
    selectedItem,
    removePatientFromCartItem,
  } = useContext(CartContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useStore();

  const user = getUser();
  const [isEditorOpen, setIsEditorOpen] = useState(false);

  const tags = ['Self', 'Father', 'Mother', 'Brother', 'Sister', 'Daughter'];

  const unusedTags = [
    ...tags?.filter(tag => !patients?.some(p => p.tag === tag)),
    'Other',
  ];
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  // const [tickedPatients, setTickedPatients] = useState([]);
  const [patientsToShow, setPatientsToShow] = useState([]);
  const [tag, setTag] = useState(unusedTags[0]);

  const getGenderFromTag = tag => {
    return ['Mother', 'Sister', 'Daughter'].includes(tag) ? 'female' : 'male';
  };

  const [gender, setGender] = useState(getGenderFromTag(unusedTags[0]));
  const [selectedTabIndex, setSelectedTabIndex] = useState(
    gender === 'male' ? 0 : 1
  );

  const [newPatient, setNewPatient] = useState({
    name: '',
    birthdate: '',
    gender: gender,
    tag: tag,
  });

  const onGenderChange = gender => {
    setGender(gender);
    genderTabHandler(gender);
  };

  const onTagChange = tag => {
    setTag(tag);
    const newGender = getGenderFromTag(tag);
    setGender(newGender);
    
    if (tag === 'Self' && user) {
      setNewPatient({
        name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        birthdate: user.dateOfBirth ? new Date(user.dateOfBirth).toISOString().split('T')[0] : '',
        gender: user.gender || newGender,
        tag: tag
      });
      if (user.gender) {
        setGender(user.gender);
        genderTabHandler(user.gender);
      }
    } else {
      setNewPatient({
        name: '',
        birthdate: '',
        gender: newGender,
        tag: tag
      });
    }
  };

  const genderTabHandler = gender => {
    setSelectedTabIndex(gender === 'male' ? 0 : 1);
  };

  useEffect(() => {
    genderTabHandler(gender);
  }, [gender]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('openEditor') === 'true' && addPatientsDrawer) {
      setIsEditorOpen(true);
    }
  }, [location, addPatientsDrawer]);

  const onClose = () => {
    setAddPatientsDrawer(false);
    setIsEditorOpen(false);
  };

  console.log(
    'calling select tab index,gender,tag',
    selectedTabIndex,
    gender,
    tag
  );

  useEffect(() => {
    const updatedPatients = patients?.map(patient => {
      const isSelected = selectedItem?.patients?.some(p => p?._id === patient?._id);
      return { ...patient, checked: !!isSelected };
    });
    setPatientsToShow(updatedPatients);
  }, [patients, selectedItem]);

  // checkbox handler

  const handleCheckboxChange = patient => {
    const isSelected = selectedItem?.patients?.some(p => p._id === patient._id);
    if (isSelected) {
      removePatientFromCartItem(selectedItem, patient);
    } else {
      addPatientToCartPlan(patient);
    }
  };

  const handleSubmit = async () => {
    if (!user?._id) {
      toast({
        title: 'Login Required',
        description: 'Please login to add a patient to your account.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!newPatient.name || !newPatient.birthdate) {
      toast({
        title: 'Missing Details',
        description: 'Please fill name and date of birth.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      const patient = { ...newPatient, tag: tag, gender: gender };
      const res = await axios.post(
        `${process.env.REACT_APP_SERVER}/addPatient`,
        {
          userId: user?._id,
          ...patient,
        }
      );

      // If the patient is 'Self', update the user profile too
      if (tag === 'Self') {
        try {
          await axios.put(
            `${process.env.REACT_APP_SERVER}/user/${user?._id}`,
            {
              dateOfBirth: patient.birthdate,
              gender: patient.gender,
            }
          );
          // console.log('User profile synced with Self patient data');
        } catch (syncError) {
          console.error('Failed to sync user profile:', syncError);
        }
      }

      addPatient(res?.data?.user?.patients);
      setPatientsToShow(res?.data?.user?.patients);

      setUser(res.data.user);
      localStorage.setItem('user', JSON.stringify(res.data.user));

      setNewPatient({ name: '', birthdate: '', gender: gender, tag: tag });
      setIsEditorOpen(false);
      toast({
        title: tag === 'Self' ? 'Profile & Patient Updated' : 'Patient Added',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      fetchPatient();
    } catch (error) {
      toast({
        title: 'Error Adding Patient',
        description: error?.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchPatient = useCallback(async () => {
    try {
      setLoading(true);
      if (user?._id) {
        const res = await axios.get(
          `${process.env.REACT_APP_SERVER}/getUser/${user._id}`
        );

        addPatient(res?.data?.user?.patients);
        setPatientsToShow(() => res?.data?.user?.patients);
      }
    } catch (error) {
      console.error('error while fetching patient', error);
    } finally {
      setLoading(false);
    }
  }, [user?._id, addPatient]);

  useEffect(() => {
    fetchPatient();
  }, [fetchPatient]);

  const calculateAge = birthdate => {
    const birthDate = new Date(birthdate);
    const currentDate = new Date();
    const ageInMilliseconds = currentDate - birthDate;
    const ageInYears = ageInMilliseconds / (365 * 24 * 60 * 60 * 1000);
    return Math.floor(ageInYears);
  };

  return (
    <>
      <Drawer
        size="sm"
        isOpen={addPatientsDrawer}
        placement="right"
        onClose={onClose}
        finalFocusRef={btnRef}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Add or Select Patient</DrawerHeader>

          <DrawerBody
            sx={{
              display: 'flex',
              flexDir: 'column',
              gap: 3,
            }}
          >
            <Box
              bg="teal.50"
              p={4}
              borderRadius="xl"
              border="1px dashed"
              borderColor="teal.200"
              display={isEditorOpen ? 'none' : 'flex'}
              alignItems="center"
              gap={3}
              cursor="pointer"
              transition="all 0.2s"
              _hover={{ bg: 'teal.100', transform: 'translateY(-2px)' }}
              onClick={() => {
                if (!user?._id) {
                  navigate(`/signin?redirect=${encodeURIComponent('/cart?openDrawer=true&openEditor=true')}`);
                  return;
                }
                setIsEditorOpen(!isEditorOpen);
              }}
            >
              <Avatar size="xs" bg="teal.500" />
              <Text fontSize="sm" fontWeight="bold" color="teal.700">
                + Add New Family Member
              </Text>
            </Box>
            <Box
              bg="gray.50"
              borderRadius="2xl"
              p={5}
              border="1px solid"
              borderColor="gray.100"
              display={isEditorOpen ? 'block' : 'none'}
              boxShadow="sm"
            >
              <Flex direction="column" gap={4}>
                <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase">
                  Patient Information
                </Text>

                <Tabs size="sm" variant="soft-rounded" colorScheme="teal" index={unusedTags.indexOf(tag)}>
                  <TabList overflowX="auto" pb={2}>
                    {unusedTags.map((t, i) => (
                      <Tab key={i} onClick={() => onTagChange(t)} fontSize="xs">
                        {t}
                      </Tab>
                    ))}
                  </TabList>
                </Tabs>

                <Input
                  bg="white"
                  borderRadius="lg"
                  placeholder="Full Name"
                  size="md"
                  value={newPatient.name}
                  onChange={e => setNewPatient({ ...newPatient, name: e.target.value })}
                />

                <Flex gap={3}>
                  <Box flex={1}>
                    <Text fontSize="xs" mb={1} ml={1} color="gray.500">Date of Birth</Text>
                    <Input
                      bg="white"
                      type="date"
                      borderRadius="lg"
                      size="md"
                      value={newPatient.birthdate}
                      onChange={e => setNewPatient({ ...newPatient, birthdate: e.target.value })}
                    />
                  </Box>
                  <Box>
                    <Text fontSize="xs" mb={1} ml={1} color="gray.500">Gender</Text>
                    <Tabs
                      variant="unstyled"
                      size="md"
                      index={selectedTabIndex}
                      onChange={index => setSelectedTabIndex(index)}
                    >
                      <TabList bg="gray.200" borderRadius="lg" p={1}>
                        <Tab
                          borderRadius="md"
                          fontSize="sm"
                          isDisabled={['Mother', 'Sister', 'Daughter'].includes(tag)}
                          _selected={{ color: 'white', bg: 'teal.500', shadow: 'md' }}
                          onClick={() => onGenderChange('male')}
                          px={4}
                        >
                          M
                        </Tab>
                        <Tab
                          borderRadius="md"
                          fontSize="sm"
                          isDisabled={['Brother', 'Father'].includes(tag)}
                          _selected={{ color: 'white', bg: 'teal.500', shadow: 'md' }}
                          onClick={() => onGenderChange('female')}
                          px={4}
                        >
                          F
                        </Tab>
                      </TabList>
                    </Tabs>
                  </Box>
                </Flex>

                <HStack justify="end" pt={2} spacing={3}>
                  <Button variant="ghost" size="sm" onClick={() => setIsEditorOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    isLoading={loading}
                    colorScheme="teal"
                    size="sm"
                    px={6}
                    borderRadius="lg"
                    onClick={handleSubmit}
                  >
                    Save Patient
                  </Button>
                </HStack>
              </Flex>
            </Box>

            {loading ? (
              <>
                <Box
                  width={'100%'}
                  display={'flex'}
                  alignItems={'center'}
                  justifyContent={'center'}
                >
                  <Spinner width={'10'} h={'10'} thickness="4px" color="teal" />
                </Box>
                <Text fontWeight={'bold'}>Patients List is Loading ...</Text>
              </>
            ) : (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    flexDir: 'column',
                    gap: 3,
                    overflowY: 'scroll',
                  }}
                >
                  {patientsToShow?.map((patient, index) => (
                    <Box
                      key={index}
                      sx={{
                        border: '3px solid #f2f2f2',
                        display: 'flex',
                        px: 4,
                        py: 3,
                        borderRadius: 10,
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Flex gap={3}>
                        <Avatar size="sm" />
                        <Box>
                          <Text fontSize={14} fontWeight={600}>
                            {patient?.name}
                          </Text>
                          <Text fontSize={12} color="gray.600">
                            {`${patient?.tag}, ${patient?.gender
                              }, ${calculateAge(patient?.birthdate)} Years`}
                          </Text>
                        </Box>
                      </Flex>
                      <Checkbox
                        colorScheme="teal"
                        isChecked={selectedItem?.patients?.some(p => p._id === patient._id)}
                        onChange={() => handleCheckboxChange(patient)}
                      />
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </DrawerBody>

          <DrawerFooter>
            <Button variant="outline" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="teal" onClick={onClose}>
              Done
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
};
